import history from '../history';
import auth0 from 'auth0-js';
import jwt_decode from 'jwt-decode';
import { action, observable, computed, flow } from 'mobx';
import { useOidc } from '@axa-fr/react-oidc';
import {
  login,
  password,
  authenticate,
  gp2Enabled,
  logUserLogin,
  tokenexchange,
  tokenexchangewithheader,
  validateUser,
  gp2EnabledCompany
} from '../apis/AuthApi';
import StorageHelper from '../utils/StorageHelper';

const { PUBLIC_URL } = process.env;
const storageHelper = new StorageHelper();

export default class AuthStore {
  tokenRenewalTimeout = null;

  // All the items we keep in storage which identify the user.
  storedUserItems = [
    'email',
    'username',
    'access_token',
    'id_token',
    'id_token_info',
    'expires_at',
    'driverLoginRememberMeInfo',
    'permissions',
    'company_hash'
  ];

  @observable
  loginInfo = {
    company: undefined,
    userName: undefined,
    password: undefined,
    email: undefined,
    appLauncher: false,
    rememberMe: undefined
  };

  @observable
  passwordInfo = {
    company: undefined,
    userName: undefined,
    oldPassword: undefined,
    newPassword: undefined,
    confirmNewPassword: undefined
  };

  @observable auth0 = undefined;
  @observable idToken = '';
  @observable accessToken = '';
  @observable expiresAt = 0;
  @observable
  userInfo = { userName: undefined, email: undefined, companyHash: undefined };

  @observable
  loading = {
    login: false
  };

  /**
   *
   * @param {RootStore} rootStore The instance of the rootstore for the application.
   */
  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  async init() {
    try {
      const auth0Domain = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.auth0Domain
      ];
      const auth0ClientID = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.auth0ClientID
      ];
      const auth0Audience = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.auth0Audience
      ];

      this.auth0 = await new auth0.WebAuth({
        domain: auth0Domain,
        clientID: auth0ClientID,
        redirectUri:
          'https://' + window.location.host + PUBLIC_URL + '/callback',
        audience: auth0Audience,
        responseType: 'token id_token'
      });
    } catch (e) {
      console.log(`auth0 config async catch triggered with exception ${e}`);
    }

    if (storageHelper.getItem('expires_at')) {
      try {
        this.expiresAt = JSON.parse(storageHelper.getItem('expires_at'));
      } catch (e) {}
    }

    if ((await this.isSso()) && this.expiresAt) {
      this.startSilentReauthTimer(
        this.getExpiresInFromExpiresAt(this.expiresAt)
      );
    }

    this.loadAccessTokenFromLocalStorage();
    this.loadIdTokenFromLocalStorage();
  }

  async isSso() {
    const idTokenInfo = JSON.parse(this.getIdTokenInfoFromLocalStorage());

    if (!idTokenInfo) {
      return false;
    }

    const auth0Domain = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.auth0Domain
    ];

    if (!auth0Domain) {
      return false;
    }

    const iss = idTokenInfo.iss;

    return new RegExp(
      `https?://${auth0Domain.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}/?`
    ).test(iss);
  }

  @computed
  get isLoggedIn() {
    return !!this.userInfo.userName;
  }

  /**
   * This method is not a @computed getter because it can modify the state, and @action's can not be @computed.
   */
  @action
  getUserName() {
    if (!this.userInfo.userName) {
      this.initUserInfo();
    }

    return this.userInfo.userName;
  }

  @action
  getCompanyHash() {
    if (!this.userInfo.companyHash) {
      this.initUserInfo();
    }

    return this.userInfo.companyHash;
  }

  @action
  loadAccessTokenFromLocalStorage() {
    const token = this.getAccessTokenFromLocalStorage();
    if (token) {
      this.accessToken = token;
    }
  }

  @action
  loadIdTokenFromLocalStorage() {
    const token = this.getIdTokenFromLocalStorage();
    if (token) {
      this.idToken = token;
    }
  }

  @action
  handleLoginError(error) {
    if (!error.response) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message: error.message
      });

      return;
    }

    const status = error.response.status;

    // todo: all of these need to be translated
    if (status === 401) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message: 'The company name, user name, or password is incorrect.'
      });
    } else if (status === 403) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message:
          'Please contact your manager to obtain permission to access the Response Center.'
      });
    } else if (status === 400) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'info',
        message: 'The company name, user name, or password is missing.'
      });
    } else if (status === 404) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'warning',
        message: 'The system is not currently available. Try again later.'
      });
    } else if (status === 500) {
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message: 'The system is not currently available. Try again later.'
      });
    }
  }

  addErrorsToNotifications(errors = []) {
    if (!errors.length) {
      return;
    }

    this.rootStore.notificationsStore.pushNotification({
      type: 'error',
      message: errors.join(', ')
    });
  }

  addGP2ErrorsToNotifications(errors = []) {
    if (!errors.length) {
      return;
    }

    this.rootStore.notificationsStore.pushNotification({
      type: 'error',
      message: `${errors[0].message}`
    });
  }

  finalizeLoginProcess() {
    this.loading.login = false;
    this.loginInfo.rememberMe
      ? this.createRememberMeStorage()
      : this.deleteRememberMeStorage();
  }

  next = flow(function*() {
    this.rootStore.notificationsStore.clearNotifications();
    this.loading.login = true;

    if (this.loginInfo.email == null || this.loginInfo.email.trim() === '') {
      this.rootStore.notificationsStore.pushNotification({
        type: 'info',
        message: 'The user name or email is missing.'
      });
      this.loading.login = false;
      return;
    }

    const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (validateEmail(this.loginInfo.email.trim())) {
      try {
        const authenticationServiceUrl = this.rootStore.configStore.config[
          this.rootStore.configStore.configurationKeys.authenticationServiceUrl
        ];

        const gp2enabledResponse = yield gp2Enabled(
          authenticationServiceUrl,
          this.loginInfo.email.trim()
        );

        const responseErrors = gp2enabledResponse.data.errors || [];

        if (responseErrors.length) {
          this.addGP2ErrorsToNotifications(responseErrors);
          return;
        }

        if (gp2enabledResponse.data && gp2enabledResponse.data.isGp2Enabled) {
          window.open(
            `/safetysecure?login_hint=${encodeURIComponent(
              this.loginInfo.email.trim()
            )}`,
            '_self'
          );
        } else {
          history.push(`/login`);
        }
      } catch (error) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'error',
          message: `${error}`
        });
      } finally {
        this.finalizeLoginProcess();
      }
    } else {
      if (this.loginInfo.email && this.loginInfo.email.trim() !== '') {
        history.push(`/login`);
      } else {
        this.rootStore.notificationsStore.pushNotification({
          type: 'info',
          message: `Please enter your username or email address`
        });
      }
      this.finalizeLoginProcess();
    }
  });

  login = flow(function*() {
    this.rootStore.notificationsStore.clearNotifications();
    this.loading.login = true;

    try {
      const authenticationServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.authenticationServiceUrl
      ];

      if (
        this.loginInfo.company == null ||
        this.loginInfo.company.trim() === ''
      ) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'info',
          message: 'The company name, user name, or password is missing.'
        });
        return;
      }

      const gp2enabledResponse = yield gp2EnabledCompany(
        authenticationServiceUrl,
        this.loginInfo.company
      );

      if (gp2enabledResponse.data && gp2enabledResponse.data.isGp2Enabled) {
        this.loginInfo.gp2Enabled = true;
        this.rootStore.notificationsStore.pushNotification({
          type: 'error',
          message:
            'Email login is required. Please click on back and try again with your email.'
        });
        return;
      }

      const loginResponse = yield login(
        authenticationServiceUrl,
        this.loginInfo
      );

      // Ideally these errors will be caught in the API, thrown there, so this store doesn't have to deal with checking if there are errors.
      const responseErrors =
        loginResponse.data.errors || gp2enabledResponse.data.errors || [];

      if (responseErrors.length) {
        this.addErrorsToNotifications(responseErrors);
        return;
      }

      this.processTokens(
        loginResponse.data.accessToken,
        loginResponse.data.idToken
      );

      yield this.rootStore.authZStore.getPermissions();

      if (!(yield this.rootStore.authZStore.isAuthorized())) {
        this.removeStorageForLogout();
        this.rootStore.authZStore.handleUnauthorized();
      } else {
        if (this.loginInfo.rememberMe) {
          this.createRememberMeStorage();
        } else {
          this.deleteRememberMeStorage();
        }

        this.handleLoginComplete();
      }
    } catch (error) {
      this.handleLoginError(error);
    } finally {
      this.loading.login = false;
      this.loginInfo.password = '';
    }
  });

  @action
  processTokens(accessToken, idToken) {
    this.accessToken = accessToken;
    this.idToken = idToken;
    this.storeAccessToken(this.accessToken);
    this.storeIdToken(this.idToken);
    this.storeExpiresAt(this.idToken);
    this.initUserInfoFromToken(this.idToken);
    this.storeUserInfo();
  }

  @action
  handleLoginComplete() {
    this.logUserLogin();
    history.replace('/safety');
  }

  logUserLogin() {
    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.authenticationServiceUrl
    ];

    const idTokenHeader = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.idTokenCustomHeader
    ];

    logUserLogin(
      authenticationServiceUrl,
      this.accessToken,
      this.idToken,
      idTokenHeader
    );
  }

  @action
  loginAuth0() {
    this.auth0.authorize();
  }

  @action
  async handleAuth0Authentication(resolve, reject) {
    await this.auth0.parseHash(
      {
        __enableIdPInitiatedLogin: true //Here is where we added this variable in order to get it to work so that it does not match for state which doesn't appear
      },
      async (err, authResult) => {
        if (err) {
          this.handleUnsuccessfulAuth0Authentication(err);
          return;
        }
        await this.handleSuccessfulAuth0Authentication(authResult);
      }
    );
  }

  @action
  handleTokenAuthentication = flow(function*(accessToken, idToken) {
    if (!accessToken || !idToken) {
      // Can't get in.
      this.rootStore.authZStore.handleUnauthorized(false);
      return;
    }

    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.authenticationServiceUrl
    ];

    const idTokenHeader = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.idTokenCustomHeader
    ];

    this.processTokens(accessToken, idToken);

    yield this.rootStore.authZStore.getPermissions();

    if (
      !(yield authenticate(
        authenticationServiceUrl,
        accessToken,
        idToken,
        idTokenHeader
      )) ||
      !(yield this.rootStore.authZStore.isAuthorized())
    ) {
      this.removeStorageForLogout();
      this.rootStore.authZStore.handleUnauthorized();
    } else {
      this.handleLoginComplete();
    }
  });

  @action
  handleGP2Authentication = flow(function*(accessToken, idToken) {
    const { logout, isAuthenticated } = useOidc();

    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.authenticationServiceUrl
    ];

    const idTokenHeader = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.idTokenCustomHeader
    ];

    // if the user is not gp2 authenticated
    if (!isAuthenticated) {
      this.rootStore.authZStore.handleUnauthorized(false);
      return;
    }

    // if gp2 access token or id token is not available
    if (!accessToken || !idToken) {
      // Can't get in.
      logout('/unauthorized');
      this.removeStorageForLogout();
      return;
    }

    try {
      const response = yield tokenexchangewithheader(
        authenticationServiceUrl,
        accessToken,
        idToken,
        idTokenHeader
      );

      const responseData = response && response.data ? response.data : null;
      const { accessToken: newAccessToken, idToken: newIdToken } =
        responseData || {};

      // if sd access token or id token is not available
      if (!newAccessToken || !newIdToken) {
        // Can't get in.
        logout('/unauthorized');
        this.removeStorageForLogout();
        return;
      }

      const validUserResp = yield validateUser(
        authenticationServiceUrl,
        newAccessToken,
        idTokenHeader
      );

      if (
        !validUserResp ||
        !validUserResp.data ||
        !validUserResp.data.isValidUser
      ) {
        this.removeStorageForLogout();
        this.rootStore.authZStore.handleUnauthorized(false);
        return;
      }

      this.processTokens(newAccessToken, newIdToken);
      this.startSilentGp2ReAuth();

      yield this.rootStore.authZStore.getPermissions();

      if (!(yield this.rootStore.authZStore.isAuthorized())) {
        // if user is not authorized
        logout('/unauthorized');
        this.removeStorageForLogout();
      } else {
        this.handleLoginComplete();
      }
    } catch (error) {
      if (isAuthenticated) {
        logout('/unauthorized');
        this.removeStorageForLogout();
        return;
      } else {
        this.rootStore.authZStore.handleUnauthorized(false);
        return;
      }
    }
  });

  @action
  handleAppLAuthentication = flow(function*() {
    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.fleetAuthenticationUrl
    ];

    const idTokenHeader = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.idTokenCustomHeader
    ];

    try {
      const response = yield tokenexchange(authenticationServiceUrl);
      const accessToken =
        response && response.data ? response.data.accessToken : null;
      const idToken = response && response.data ? response.data.idToken : null;

      // if gp2 cookie/sd token is not available
      if (!accessToken || !idToken) {
        this.rootStore.authZStore.handleUnauthorized(false);
        return;
      }

      const validUserResp = yield validateUser(
        authenticationServiceUrl,
        accessToken,
        idTokenHeader
      );

      if (
        !validUserResp ||
        !validUserResp.data ||
        !validUserResp.data.isValidUser
      ) {
        this.removeStorageForLogout();
        this.rootStore.authZStore.handleUnauthorized(false);
        return;
      }

      this.processTokens(accessToken, idToken);
      this.startSilentAppLReAuth();

      yield this.rootStore.authZStore.getPermissions();

      if (!(yield this.rootStore.authZStore.isAuthorized())) {
        // if user is not authorized
        this.removeStorageForLogout();
        this.rootStore.authZStore.handleUnauthorized(false);
      } else {
        this.handleLoginComplete();
      }
    } catch (error) {
      this.rootStore.authZStore.handleUnauthorized(false);
    }
  });

  @action
  async handleSuccessfulAuth0Authentication(authResult) {
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken;
    this.storeAccessToken(this.accessToken);
    this.storeExpiresAtAuth0(authResult.expiresIn);
    this.startSilentReauthTimer(authResult.expiresIn);
    this.initUserInfoFromToken(authResult.idToken);
    this.storeIdToken(authResult.idToken);

    if (!(await this.rootStore.authZStore.getLoginEnabled())) {
      this.removeStorageForLogout();
      this.rootStore.authZStore.handleUnauthorized(true);
    } else {
      await this.rootStore.authZStore.getPermissions();

      if (!(await this.rootStore.authZStore.isAuthorized())) {
        this.removeStorageForLogout();
        this.rootStore.authZStore.handleUnauthorized(true);
      } else {
        this.storeUserInfo();
        this.handleLoginComplete();
      }
    }
  }

  @action
  handleUnsuccessfulAuth0Authentication(authError) {
    // resolves descrepancy between name of error description fields
    // returned from checkSession and parseHash functions
    if (authError.error_description && !authError.errorDescription) {
      authError.errorDescription = authError.error_description;
    }

    if (
      authError.errorDescription ===
      this.rootStore.configStore.config.auth0AccessDeniedErrorMessage
    ) {
      this.removeStorageForLogout();
      this.rootStore.authZStore.handleUnauthorized(true);
    } else {
      this.logout();
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message: `Auth0: ${authError.error} - ${authError.errorDescription}. Check the console for further details.`
      });
    }
  }

  handlePasswordError(error) {
    if (error.response) {
      const status = error.response.status;
      if (status === 401 || status === 400) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'error',
          message: 'Current password is incorrect'
        });
      } else if (status === 404) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'warning',
          message: 'Oops! Something went wrong. Please try again later.'
        });
      } else if (status === 500) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'error',
          message: 'Oops! Something went wrong. Please try again later.'
        });
      }
    } else {
      this.rootStore.notificationsStore.pushNotification({
        type: 'error',
        message: error.message
      });
    }
  }

  password = flow(function*() {
    this.rootStore.notificationsStore.clearNotifications();
    let success = false;

    try {
      const authenticationServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.authenticationServiceUrl
      ];

      const idTokenHeader = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.idTokenCustomHeader
      ];

      const response = yield password(
        authenticationServiceUrl,
        this.accessToken,
        this.idToken,
        idTokenHeader,
        this.passwordInfo
      );

      if (response.data && response.data.errors) {
        this.rootStore.notificationsStore.pushNotification({
          type: 'error',
          message: response.data.errors.join(', ')
        });
      } else {
        if (response.status === 200) {
          this.rootStore.notificationsStore.pushNotification({
            type: 'success',
            message: 'Your password has been successfully updated!'
          });
          success = true;
        }
      }
    } catch (error) {
      this.handlePasswordError(error);
    }

    return success;
  });

  composeChangePasswordURI() {
    const { config, configurationKeys } = this.rootStore.configStore;
    const values = {
      oidcChangePasswordEndpoint:
        config[configurationKeys.oidcChangePasswordEndpoint],
      oidcResponseType: config[configurationKeys.oidcResponseType],
      oidcCodeChallengeMethod:
        config[configurationKeys.oidcCodeChallengeMethod],
      oidcClientId: config[configurationKeys.oidcClientId]
    };
    const {
      oidcChangePasswordEndpoint,
      oidcResponseType,
      oidcClientId,
      oidcCodeChallengeMethod
    } = values;

    return `${oidcChangePasswordEndpoint}?response_type=${oidcResponseType}&client_id=${oidcClientId}&redirect_uri=${encodeURIComponent(
      window.location.origin
    )}&scope=${oidcClientId}&code_challenge=null&code_challenge_method=${oidcCodeChallengeMethod}`;
  }

  changePassword() {
    this.rootStore.notificationsStore.clearNotifications();
    sessionStorage.setItem('externalRedirect', true);
    window.location.href = this.composeChangePasswordURI();
  }

  @action
  logout() {
    if (!this.loginInfo['appLauncher']) {
      const idTokenInfo =
        JSON.parse(this.getIdTokenInfoFromLocalStorage()) || {};
      this.removeStorageForLogout();
      if (idTokenInfo && idTokenInfo.sub) {
        const connectionName = idTokenInfo.sub.split('|')[1] || '';
        if (connectionName && connectionName.length > 0) {
          this.auth0.authorize({
            connection: connectionName
          });
        }
      } else {
        history.push(`/`);
      }
    }
  }

  @computed
  get isAuthenticated() {
    // Check whether the current time is past the access token's expiry time.
    return new Date().getTime() < this.expiresAt;
  }

  @action
  initUserInfoFromToken(token) {
    if (token) {
      try {
        const decodedTokenObject = jwt_decode(token);
        if (decodedTokenObject) {
          this.userInfo.email =
            decodedTokenObject['https://smartdrivesystems.com/email'] ||
            decodedTokenObject.email;
          this.userInfo.userName =
            decodedTokenObject['https://smartdrivesystems.com/username'] ||
            decodedTokenObject.email;
          this.userInfo.companyHash =
            decodedTokenObject['https://smartdrivesystems.com/company_hash'] ||
            decodedTokenObject['ext_business_account'];
        }
      } catch (invalidTokenError) {
        this.userInfo = {};
      }
    }
  }

  @action
  initUserInfo() {
    this.initUserInfoFromToken(this.getIdTokenFromLocalStorage());
  }

  removeStorageForLogout() {
    this.deleteAccessToken();
    this.deleteExpiresAt();
    this.deleteIdToken();
    this.deleteUserInfo();
    clearTimeout(this.tokenRenewalTimeout);
    this.idToken = '';
    this.accessToken = '';
    this.expiresAt = 0;
    this.userInfo = {
      userName: undefined,
      email: undefined,
      companyHash: undefined
    };
    this.rootStore.notificationsStore.clearNotifications();
  }

  getAccessTokenFromLocalStorage() {
    return storageHelper.getItem('access_token');
  }

  getIdTokenFromLocalStorage() {
    return storageHelper.getItem('id_token');
  }

  getIdTokenInfoFromLocalStorage() {
    return storageHelper.getItem('id_token_info');
  }

  storeAccessToken(token) {
    storageHelper.setItem('access_token', token);
  }

  deleteAccessToken() {
    storageHelper.removeItem('access_token');
  }

  storeIdToken(token) {
    storageHelper.setItem('id_token', token);
    storageHelper.setItem('id_token_info', JSON.stringify(jwt_decode(token)));
  }

  deleteIdToken() {
    storageHelper.removeItem('id_token');
    storageHelper.removeItem('id_token_info');
  }

  storeExpiresAt(token) {
    const decodedTokenObject = jwt_decode(token);
    this.expiresAt = 0;
    if (decodedTokenObject && decodedTokenObject.exp) {
      this.expiresAt = decodedTokenObject.exp * 1000;
    } else {
      this.expiresAt = 60 * 1000 + new Date().getTime();
    }

    storageHelper.setItem('expires_at', JSON.stringify(this.expiresAt));
  }

  storeExpiresAtAuth0(expiresIn) {
    this.expiresAt = 0;
    if (expiresIn) {
      this.expiresAt = expiresIn * 1000 + new Date().getTime();
    } else {
      this.expiresAt = 60 * 1000 + new Date().getTime();
    }

    storageHelper.setItem('expires_at', JSON.stringify(this.expiresAt));
  }

  getExpiresInFromExpiresAt(expiresAt) {
    return (expiresAt - new Date().getTime()) / 1000;
  }

  deleteExpiresAt() {
    storageHelper.removeItem('expires_at');
    this.expiresAt = 0;
  }

  storeUserInfo() {
    storageHelper.setItem('email', this.userInfo.email);
    storageHelper.setItem('username', this.userInfo.userName);
    storageHelper.setItem('company_hash', this.userInfo.companyHash);
  }

  @action
  deleteUserInfo() {
    storageHelper.removeItem('email');
    storageHelper.removeItem('username');
    storageHelper.removeItem('company_hash');

    this.userInfo.companyHash = undefined;
    this.userInfo.userName = undefined;
  }

  createRememberMeStorage() {
    const rememberMeInfo = {
      company: this.loginInfo.company,
      userName: this.loginInfo.userName,
      email: this.loginInfo.email
    };
    storageHelper.setItem(
      'driverLoginRememberMeInfo',
      JSON.stringify(rememberMeInfo)
    );
  }

  deleteRememberMeStorage() {
    storageHelper.removeItem('driverLoginRememberMeInfo');
    this.loginInfo.company = '';
    this.loginInfo.userName = '';
    this.loginInfo.email = '';
  }

  initRememberMeFromStorage() {
    const rememberMeInfoString = storageHelper.getItem(
      'driverLoginRememberMeInfo'
    );

    if (!rememberMeInfoString) {
      return;
    }

    const rememberMeInfo = JSON.parse(rememberMeInfoString);

    if (rememberMeInfo.company) {
      this.loginInfo.company = rememberMeInfo.company;
      this.loginInfo.rememberMe = true;
    }

    if (rememberMeInfo.userName) {
      this.loginInfo.userName = rememberMeInfo.userName;
      this.loginInfo.rememberMe = true;
    }

    if (rememberMeInfo.email) {
      this.loginInfo.email = rememberMeInfo.email;
      this.loginInfo.rememberMe = true;
    }
  }

  @action
  async renewToken() {
    this.auth0.checkSession({}, async (error, result) => {
      if (error) {
        this.handleUnsuccessfulAuth0Authentication(error);
        return;
      }

      await this.handleSuccessfulAuth0Authentication(result);
    });
  }

  startSilentReauthTimer(expiresInSeconds) {
    /**
     * The delay will be halfway between now and the expiry time.
     */
    const delayInMilliseconds = (expiresInSeconds / 2) * 1000;

    if (delayInMilliseconds > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewToken();
      }, delayInMilliseconds);
    }
  }

  @action
  async renewGP2TokenExchange(accessToken, idToken) {
    const { isAuthenticated, logout } = useOidc();
    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.authenticationServiceUrl
    ];

    const idTokenHeader = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.idTokenCustomHeader
    ];

    try {
      if (accessToken && idToken && isAuthenticated && this.accessToken) {
        const response = await tokenexchangewithheader(
          authenticationServiceUrl,
          accessToken,
          idToken,
          idTokenHeader
        );

        const responseData = response && response.data ? response.data : null;
        const { accessToken: newAccessToken, idToken: newIdToken } =
          responseData || {};

        if (newAccessToken && newIdToken) {
          this.processTokens(newAccessToken, newIdToken);
          this.startSilentGp2ReAuth();
        }
      } else {
        logout('/');
        this.removeStorageForLogout();
        return;
      }
    } catch (error) {
      logout('/');
      this.removeStorageForLogout();
      return;
    }
  }

  startSilentGp2ReAuth() {
    const accessTokenObj = JSON.parse(localStorage.getItem('oidc.default'));
    const issueTime =
      accessTokenObj && accessTokenObj.tokens
        ? accessTokenObj.tokens.issuedAt
        : null;

    const translateTime =
      issueTime +
      this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.oidcRefreshTimeInSec
      ] +
      this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.oidcOffsetTimeInSec
      ];

    const delay = translateTime - Math.floor(Date.now() / 1000);

    if (delay > 0) {
      setTimeout(() => {
        const accessTokenObj = JSON.parse(localStorage.getItem('oidc.default'));
        const accessToken =
          accessTokenObj && accessTokenObj.tokens
            ? accessTokenObj.tokens.accessToken
            : null;
        const idToken =
          accessTokenObj && accessTokenObj.tokens
            ? accessTokenObj.tokens.idToken
            : null;
        const expiresInSeconds =
          accessTokenObj && accessTokenObj.tokens
            ? accessTokenObj.tokens.expiresIn
            : null;
        this.renewGP2TokenExchange(accessToken, idToken, expiresInSeconds);
      }, delay * 1000);
    }
  }

  @action
  async renewAppLTokenExchange() {
    const authenticationServiceUrl = this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.fleetAuthenticationUrl
    ];

    try {
      const response = await tokenexchange(authenticationServiceUrl);
      const accessToken =
        response && response.data ? response.data.accessToken : null;
      const idToken = response && response.data ? response.data.idToken : null;

      if (accessToken && idToken && this.accessToken) {
        this.processTokens(accessToken, idToken);
        this.startSilentAppLReAuth();
      } else {
        this.rootStore.authZStore.handleUnauthorized(false);
        return;
      }
    } catch (error) {
      this.rootStore.authZStore.handleUnauthorized(false);
      return;
    }
  }

  startSilentAppLReAuth() {
    const appLRefreshTimeInMilliSeconds =
      this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.appLRefreshTimeInSec
      ] * 1000;

    if (appLRefreshTimeInMilliSeconds > 0) {
      setTimeout(() => {
        this.renewAppLTokenExchange();
      }, appLRefreshTimeInMilliSeconds);
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/AuthStore.js